"use client";

import { ExternalLink, RotateCcw } from "lucide-react";
import Image from "next/image";

import { getCurrencySymbol } from "@/features/currency/_logic/iso-4217";
import { formatTime } from "@/features/flight-estimator/_logic/utils";

import { useEstimator } from "../../_context/EstimatorContext";
import { useUndoableEdit } from "../../_hooks/useUndoableEdit";
import type { GroupFlight } from "../../_logic/city-flights";
import { CABIN_OPTIONS } from "../../_logic/constants";
import type { Cabin } from "../../_logic/types";
import { groupName } from "../../_logic/workspace";
import { PopoverSelect } from "../ui/PopoverSelect";
import { CellSkeleton } from "../workspace/CellSkeleton";
import { InlineNumberEdit } from "../workspace/InlineNumberEdit";
import { FlightField as Field } from "./FlightField";
import { safeUrl } from "../../_logic/saved/fares-schema";

interface GroupFlightRowProps {
  group: GroupFlight;
  destination: string;
  searching: boolean;
}

/**
 * One traveler group's flight: group, class, travelers, flight, time, layovers, cost per
 * traveler and total, with a link to the flight. Class, travelers and fare are editable and
 * undoable; a typed fare is marked as the planner's own.
 * @param props - The group, its destination and whether the search is still running.
 * @returns The row.
 * @throws Never.
 */
export const GroupFlightRow = ({ group, destination, searching }: GroupFlightRowProps) => {
  // Hooks
  const { workspace, money } = useEstimator();
  const apply = useUndoableEdit();

  // Derived
  const flight = group.result?.flight ?? null;
  const symbol = getCurrencySymbol(workspace.currency);
  const name = groupName(group.origin);
  const pending = searching && !group.result && !group.overridden;

  // Render
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3 py-3 pr-4 sm:grid-cols-4 sm:pr-5 lg:grid-cols-[1.3fr_1fr_0.7fr_1.2fr_0.9fr_0.9fr_1fr_1fr]">
      <Field label="Group">{name}</Field>
      <Field label="Class">
        <PopoverSelect
          ariaLabel={`Class for ${name}`}
          options={CABIN_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          value={group.cabin}
          onChange={(cabin) =>
            apply(
              { type: "setOriginCabin", key: group.key, cabin: cabin === workspace.cabin ? null : (cabin as Cabin) },
              `${name} in ${cabin}`
            )
          }
        />
      </Field>
      <Field label="Travelers">
        <InlineNumberEdit
          display={group.origin.count}
          initial={String(group.origin.count)}
          label={`Travelers in ${name}`}
          field={{ widthClass: "w-10", inputMode: "numeric" }}
          onCommit={(text) => {
            const count = Number.parseInt(text, 10);
            if (!Number.isInteger(count) || count < 1) return false;
            apply({ type: "setOriginCount", key: group.key, count }, `${name}: ${count} travelers`);
          }}
        />
      </Field>
      <Field label="Flight">
        {pending ? (
          <CellSkeleton width="w-20" />
        ) : flight?.flightNumber ? (
          <span className="inline-flex items-center gap-1.5">
            {safeUrl(flight.airlineLogo) && (
              <Image
                src={safeUrl(flight.airlineLogo)!}
                alt=""
                width={16}
                height={16}
                unoptimized
                className="size-4 object-contain"
              />
            )}
            {flight.flightNumber}
          </span>
        ) : (
          <span className="text-amber-700">{group.result?.error ?? "No flight found"}</span>
        )}
      </Field>
      <Field label="Flight time">{flight?.minutes ? formatTime(flight.minutes) : "–"}</Field>
      <Field label="Layovers">
        {flight ? `${flight.layovers}${flight.layoverMinutes ? ` · ${formatTime(flight.layoverMinutes)}` : ""}` : "–"}
      </Field>
      <Field label={group.overridden ? "Your fare" : "Per traveler"}>
        {pending ? (
          <CellSkeleton />
        ) : (
          <span className="inline-flex items-center gap-1">
            <InlineNumberEdit
              display={
                <span className={group.overridden ? "text-indigo-700" : undefined}>
                  {group.costPerTraveler === null ? "Add fare" : money.format(group.costPerTraveler)}
                </span>
              }
              initial={
                group.costPerTraveler === null
                  ? ""
                  : String(Math.round(money.fromUsd(group.costPerTraveler) ?? group.costPerTraveler))
              }
              field={{ prefix: symbol }}
              label={`Fare per traveler for ${name}`}
              onCommit={(text) => {
                const amount = Number(text.replace(/[^\d.]/g, ""));
                const usd = text && Number.isFinite(amount) ? money.toUsd(amount) : null;
                if (usd === null) return false;
                apply(
                  { type: "setFareOverride", originKey: group.key, destination, usd },
                  `${name} to ${destination} at ${money.format(usd)}`
                );
              }}
            />
            {group.overridden && (
              // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
              <button
                type="button"
                aria-label={`Use the live fare for ${name}`}
                onClick={() =>
                  apply(
                    { type: "setFareOverride", originKey: group.key, destination, usd: null },
                    `${name} back to the live fare`
                  )
                }
                className="relative after:absolute after:-inset-3 after:content-[''] rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <RotateCcw className="size-3" />
              </button>
            )}
          </span>
        )}
      </Field>
      <Field label="Total">
        <span className="inline-flex items-center gap-2">
          {group.total === null ? "–" : money.format(group.total)}
          {flight?.url && (
            <a
              href={safeUrl(flight.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              View
              <ExternalLink className="size-3" />
            </a>
          )}
        </span>
      </Field>
    </div>
  );
};
